import React, {useEffect, useRef, useState} from "react";
import {Template} from "../../../../components/template/template.jsx";
import {Card} from "../../../../components/card/card.jsx";
import Button from "../../../../components/button/button.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import SentryRow from "../../../../components/template/sentry-row.jsx";
import {useParams} from "react-router";
import {FaPlus, FaTrash} from "react-icons/fa";
import {useQuery} from "@tanstack/react-query";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import projectUtils, {clearRequiredFieldError} from "../../../../utils/project-utils.js";
import CompositionService from "../../../../services/dispatch/composition-service.js";

const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB;
const REQUIRED_BG = "!bg-[#fdfdee]";
const REQUIRED_LABEl_BG = "!bg-[linear-gradient(to_bottom,transparent_50%,#fdfdee_50%)]";

const TYPE_OPTIONS = [
    {value: "WITH_VEHICLE", label: "Com veículo"},
    {value: "WITHOUT_VEHICLE", label: "A pé"},
];

const POSITION_INPUT_CLASS = "block w-full px-2.5 py-1 text-sm bg-transparent border border-[#e5e7eb] rounded-md focus:outline-none focus:border-blue-600 h-[32px]";

const initialComposition = {type: "", name: "", vehicle: "", active: true, positions: []};

const CompositionForm = () => {
    const navigate = navigateHook();
    const {id} = useParams();
    const [state, setState] = useState(initialComposition);
    const [isSaving, setIsSaving] = useState(false);
    const [isSavingAndAdding, setIsSavingAndAdding] = useState(false);
    const positionKey = useRef(0);
    const positionsRequiredRef = useRef(null);

    const nextKey = () => `new-${positionKey.current++}`;

    const {data: vehicleOptions = []} = useQuery({
        queryFn: () => CompositionService.vehicles().then((data) => {
            const list = data?.vehicles ?? data ?? [];
            return list.map(vehicle => ({value: vehicle.id, label: vehicle.name}));
        }),
        queryKey: ["dispatch-composition-vehicles"],
    });

    const {isLoading: isQueryLoading} = useQuery({
        queryFn: async () => {
            const compResponse = await CompositionService.get(id).catch(() => null);
            if (!compResponse) {
                navigate("/404");
                return;
            }

            const composition = compResponse?.composition ?? compResponse ?? {};
            const positions = (composition.positions ?? []).map(position => ({
                key: position.id != null ? `id-${position.id}` : nextKey(),
                id: position.id,
                name: position.name ?? null,
                conductor: !!position.conductor,
                display: !!position.display,
                mandatory: position.conductor ? true : !!position.mandatory,
            }));

            setState({
                id: composition.id,
                type: composition.type ?? "",
                name: composition.name ?? "",
                vehicle: composition.vehicle ?? "",
                active: composition.active ?? true,
                positions,
            });

            return composition;
        },
        queryKey: ["dispatch-composition", id],
        enabled: !!id,
    });

    const withVehicle = state.type === "WITH_VEHICLE";
    const compositionName = withVehicle
        ? vehicleOptions.find(option => String(option.value) === String(state.vehicle))?.label ?? state.vehicle
        : state.name;

    useEffect(() => {
        if (state.positions.length > 0) clearRequiredFieldError(positionsRequiredRef.current);
    }, [state.positions.length]);

    const addPosition = () => setState(prev => ({
        ...prev,
        positions: [...prev.positions, {key: nextKey(), name: "", conductor: false, display: false, mandatory: false}],
    }));

    const removePosition = (key) => setState(prev => ({...prev, positions: prev.positions.filter(p => p.key !== key)}));

    const updatePositionName = (key, name) => setState(prev => ({
        ...prev,
        positions: prev.positions.map(p => p.key === key ? {...p, name} : p),
    }));

    const setConductor = (key) => setState(prev => ({
        ...prev,
        positions: prev.positions.map(p => p.key === key ? {...p, conductor: true, mandatory: true} : {...p, conductor: false}),
    }));

    const setDisplay = (key) => setState(prev => ({
        ...prev,
        positions: prev.positions.map(p => ({...p, display: p.key === key})),
    }));

    const toggleMandatory = (key, checked) => setState(prev => ({
        ...prev,
        positions: prev.positions.map(p => p.key === key ? {...p, mandatory: checked} : p),
    }));

    const validate = () => {
        if (!state.type) return "O campo \"Tipo\" é obrigatório.";
        if (withVehicle && !state.vehicle) return "O campo \"Veículo\" é obrigatório.";
        if (!withVehicle && !state.name) return "O campo \"Nome\" é obrigatório.";
        if (!state.positions.length) return "O campo \"Posições\" é obrigatório.";
        return "";
    };

    const buildPayload = () => ({
        type: state.type,
        name: withVehicle ? null : state.name,
        vehicle: withVehicle ? state.vehicle : null,
        active: state.active,
        positions: state.positions.map(position => ({
            ...(position.id != null ? {id: position.id} : {}),
            name: (position.name ?? "").replaceAll("'", "").replaceAll("\"", ""),
            conductor: withVehicle ? position.conductor : false,
            display: position.display,
            mandatory: position.mandatory,
        })),
    });

    const persist = (addAfter = false) => {
        const message = validate();
        if (message) {
            projectUtils.swalErrorMessage("", message, "Ok");
            return;
        }

        const payload = buildPayload();

        const setSaving = addAfter ? setIsSavingAndAdding : setIsSaving;
        setSaving(true);
        const response = id ? CompositionService.update(id, payload) : CompositionService.save(payload);
        response
            .then((data) => {
                if (addAfter) {
                    if (id) {
                        projectUtils.success(true);
                        navigate(API_DESPACHO_WEB + "/composition/form", false);
                    } else {
                        projectUtils.success(false);
                        setState(initialComposition);
                    }
                    return;
                }

                projectUtils.success(true);

                if (id) {
                    navigate(API_DESPACHO_WEB + "/composition", false);
                    return;
                }

                navigate(`${API_DESPACHO_WEB}/composition/form/${data.id}`, false);
            })
            .catch((error) => {
                console.error(error);
                const errMessage = error.response?.data?.errorMessages ?? "Não foi possível salvar.";
                projectUtils.swalErrorMessage("", errMessage, "Ok");
            })
            .finally(() => setSaving(false));
    };

    const destroy = () => {
        projectUtils.swalMessage("warning", "Excluir", `Excluir a composição "${compositionName}"?`, "Sim", true, "Não",
            ({isConfirmed}) => {
                if (!isConfirmed) return;
                CompositionService.destroy(id)
                    .then(() => {
                        projectUtils.success(true, "Excluído com sucesso");
                        navigate(API_DESPACHO_WEB + "/composition", false);
                    })
                    .catch((error) => {
                        console.error(error);
                        const errMessage = error.response?.data?.errorMessages ?? "Não foi possível excluir.";
                        projectUtils.swalErrorMessage("", errMessage, "Ok");
                    });
            });
    };

    const newComposition = () => navigate(API_DESPACHO_WEB + "/composition/form", false);

    const colSpan = withVehicle ? 5 : 4;

    return (
        <Template isLoading={isSaving || isSavingAndAdding || isQueryLoading}>
            <Template.Header>
                <Template.Title browserTitle={'Composição'}>Composição {id ? <span className="text-[12px]">(ID: {id})</span> : ""}</Template.Title>
                {id &&
                    <Button target="primary-outline" onClick={newComposition}><FaPlus/>Adicionar composição</Button>
                }
            </Template.Header>
            <Template.Body className="flex flex-col space-y-2">
                <Card>
                    <Card.Header>
                        <Card.Title>Dados</Card.Title>
                    </Card.Header>
                    <Card.Body className="flex flex-col gap-4">
                        <InputCheckbox id="active" label="Composição ativa" checked={state.active}
                                       onChange={({target}) => setState(prev => ({...prev, active: target.checked}))}/>
                        <SentryRow>
                            <SelectComponent
                                classNameWrapper={`col-span-3 rounded-md ${REQUIRED_BG}`}
                                labelClass={REQUIRED_LABEl_BG}
                                label="Tipo *"
                                options={TYPE_OPTIONS}
                                value={TYPE_OPTIONS.find(option => option.value === state.type) ?? null}
                                onChange={(option) => setState(prev => ({...prev, type: option?.value ?? ""}))}
                                isClearable={false}
                            />
                            {!withVehicle &&
                                <InputForm classNameWrapper="col-span-9" inputClass={REQUIRED_BG} id="name" label="Nome *"
                                           value={state.name}
                                           labelClass={REQUIRED_LABEl_BG}
                                           onChange={({target: {value}}) => setState(prev => ({...prev, name: value}))}/>
                            }
                            {withVehicle &&
                                <SelectComponent
                                    classNameWrapper={`col-span-9 rounded-md ${REQUIRED_BG}`}
                                    labelClass={REQUIRED_LABEl_BG}
                                    label="Veículo *"
                                    options={vehicleOptions}
                                    value={vehicleOptions.find(option => String(option.value) === String(state.vehicle)) ?? null}
                                    onChange={(option) => setState(prev => ({...prev, vehicle: option?.value ?? ""}))}
                                    isClearable={false}
                                />
                            }
                        </SentryRow>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Header className={"gap-2"}>
                        <Card.Title>Posições (Ex: Motorista, Piloto, Encarregado, etc)</Card.Title>
                        <Button className="h-6.5! w-7.5 p-0!" onClick={addPosition}>
                            <FaPlus/>
                        </Button>
                    </Card.Header>
                    <Card.Body>
                        <div className="overflow-hidden rounded-lg border border-[#D2D3D5] font-[Roboto,sans-serif]">
                            <table className="w-full text-sm border-collapse text-[#2E3742]">
                                <thead>
                                    <tr className="bg-[#efefef] text-left">
                                        <th className="h-10 px-2.5 font-bold border-b border-r border-[#D2D3D5]">Nome</th>
                                        {withVehicle && <th className="h-10 px-2.5 font-bold text-center w-[200px] border-b border-r border-[#D2D3D5]">Condutor</th>}
                                        <th className="h-10 px-2.5 font-bold text-center w-[260px] border-b border-r border-[#D2D3D5]">Responsável pela composição</th>
                                        <th className="h-10 px-2.5 font-bold text-center w-[200px] border-b border-r border-[#D2D3D5]">Obrigar preenchimento</th>
                                        <th className="h-10 px-2.5 w-[60px] border-b border-[#D2D3D5] text-center">
                                            <div
                                                ref={positionsRequiredRef}
                                                className="relative mx-auto w-fit border border-transparent rounded-lg transition-[border-color,border-width] duration-150 ease-out"
                                                data-required-field="true"
                                                data-required-has-value={state.positions.length > 0 ? "true" : "false"}
                                                data-required-focus="true"
                                                tabIndex={-1}
                                                aria-label="Posições"
                                            >
                                                <Button onClick={addPosition}>
                                                    <FaPlus/>
                                                </Button>
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {state.positions.map(position => (
                                        <tr key={position.key} className="even:bg-[#efefef] hover:bg-[#eef3fb] border-b border-[#D2D3D5] last:border-b-0">
                                            <td className="px-2.5 py-1 border-r border-[#D2D3D5]">
                                                <input className={POSITION_INPUT_CLASS} value={position.name}
                                                       onChange={(e) => updatePositionName(position.key, e.target.value)}/>
                                            </td>
                                            {withVehicle &&
                                                <td className="px-2.5 py-1 text-center border-r border-[#D2D3D5]">
                                                    <input type="radio" name="conductor" className="cursor-pointer"
                                                           checked={position.conductor}
                                                           onChange={() => setConductor(position.key)}/>
                                                </td>
                                            }
                                            <td className="px-2.5 py-1 text-center border-r border-[#D2D3D5]">
                                                <input type="radio" name="display" className="cursor-pointer"
                                                       checked={position.display}
                                                       onChange={() => setDisplay(position.key)}/>
                                            </td>
                                            <td className="px-2.5 py-1 text-center border-r border-[#D2D3D5]">
                                                <input type="checkbox" className={position.conductor ? "opacity-70" : "cursor-pointer"}
                                                       checked={position.mandatory}
                                                       disabled={position.conductor}
                                                       onChange={(e) => toggleMandatory(position.key, e.target.checked)}/>
                                            </td>
                                            <td className="px-2.5 py-1 text-center">
                                                <Button target="danger" title="Remover posição" onClick={() => removePosition(position.key)}>
                                                    <FaTrash/>
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {state.positions.length === 0 &&
                                        <tr>
                                            <td colSpan={colSpan} className="px-2.5 h-10 text-center text-gray-400">Nenhuma posição adicionada.</td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body className="flex gap-2 justify-end">
                        {id && <Button target="danger" onClick={destroy} actionFeedbackType='delete'>Excluir</Button>}
                        <Button target="primary-outline" onClick={() => persist(true)} loading={isSavingAndAdding} actionFeedbackType='save'>Salvar e adicionar novo</Button>
                        <Button onClick={() => persist(false)} loading={isSaving} actionFeedbackType='save'>Salvar</Button>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default CompositionForm;